import { useCallback, useMemo, useState } from 'react';
import type { AssessmentSchema, AnswerMap, Question } from './types';

interface Args {
  schema: AssessmentSchema;
  uuid: string;
  initialAnswers: AnswerMap;
  onAnswer?: (questionId: string, optionId: string, answers: AnswerMap) => void;
}

function visibleQuestions(schema: AssessmentSchema, answers: AnswerMap): Question[] {
  return schema.questions.filter((q) => !q.showIf || q.showIf(answers));
}

export function useAssessmentRunner({ schema, uuid, initialAnswers, onAnswer }: Args) {
  const [answers, setAnswers] = useState<AnswerMap>(initialAnswers);
  const [currentIndex, setCurrentIndex] = useState(() => {
    const visible = visibleQuestions(schema, initialAnswers);
    const idx = visible.findIndex((q) => initialAnswers[q.id] === undefined);
    return idx === -1 ? visible.length : idx;
  });

  const visible = useMemo(() => visibleQuestions(schema, answers), [schema, answers]);
  const isComplete = currentIndex >= visible.length;
  const currentQuestion = isComplete ? null : visible[currentIndex];

  const answer = useCallback(
    (questionId: string, optionId: string) => {
      setAnswers((prev) => {
        const next = { ...prev, [questionId]: optionId };
        onAnswer?.(questionId, optionId, next);
        return next;
      });
      setCurrentIndex((i) => i + 1);
    },
    [onAnswer],
  );

  const previous = useCallback(() => {
    setCurrentIndex((i) => Math.max(0, i - 1));
  }, []);

  return {
    uuid,
    answers,
    currentIndex,
    currentQuestion,
    isComplete,
    totalVisible: visible.length,
    answer,
    previous,
  };
}
